import { BusinessBusiness, HiringEvidence } from "../types";

export interface SearchQueryInfo {
  query: string;
  queryType:
    | "name_city_hiring"
    | "name_city_careers"
    | "address_jobs"
    | "name_now_hiring"
    | "facebook_hiring"
    | "instagram_jobs"
    | "opening_soon"
    | "expansion"
    | "site_careers"
    | "site_jobs"
    | "site_location";
}

/**
 * Generates targeted local search queries for a specific candidate business location.
 * Uses exact business name, address, city, state, phone, domain, brand name, and branch name.
 */
export function generateLocalHiringSearchQueries(
  business: BusinessBusiness,
  locationContext?: string
): SearchQueryInfo[] {
  const name = (business.name || "").trim();
  const address = (business.address || "").trim();
  const phone = (business.phone || "").trim();

  // Extract city/state from address or locationContext
  let city = "";
  if (address) {
    const parts = address.split(",");
    if (parts.length >= 2) {
      city = parts[parts.length - 2].trim();
    }
  }
  if (!city && locationContext) {
    city = locationContext.split(",")[0].trim();
  }
  if (!city) {
    city = "local";
  }

  // Domain extraction if official website exists
  let domain = "";
  if (business.website) {
    try {
      const parsed = new URL(business.website.startsWith("http") ? business.website : `https://${business.website}`);
      domain = parsed.hostname.replace(/^www\./, "");
    } catch {
      domain = "";
    }
  }

  const queries: SearchQueryInfo[] = [
    { query: `"${name}" "${city}" hiring`, queryType: "name_city_hiring" },
    { query: `"${name}" "${city}" careers`, queryType: "name_city_careers" },
    { query: `"${name}" "${address}" jobs`, queryType: "address_jobs" },
    { query: `"${name}" "${city}" "now hiring"`, queryType: "name_now_hiring" },
    { query: `"${name}" "${city}" Facebook hiring`, queryType: "facebook_hiring" },
    { query: `"${name}" "${city}" Instagram jobs`, queryType: "instagram_jobs" },
    { query: `"${name}" "${city}" "opening soon"`, queryType: "opening_soon" },
    { query: `"${name}" "${city}" expansion`, queryType: "expansion" },
  ];

  if (domain) {
    queries.push(
      { query: `site:${domain} careers`, queryType: "site_careers" },
      { query: `site:${domain} jobs`, queryType: "site_jobs" },
      { query: `site:${domain} "${city}"`, queryType: "site_location" }
    );
  }

  return queries;
}

/**
 * Validates whether candidate evidence belongs to the exact business and location.
 * Prevents attaching evidence from other businesses with similar names or distant cities.
 */
export function verifyEvidenceMatch(
  evidenceCandidate: {
    sourceTitle?: string;
    evidenceText?: string;
    sourceUrl?: string;
    referencedCity?: string;
    referencedAddress?: string;
    referencedPhone?: string;
    referencedDomain?: string;
  },
  business: BusinessBusiness
): {
  matchesIdentity: boolean;
  locationScope: HiringEvidence["locationScope"];
  confidence: number;
  rejectionReason?: string;
} {
  const bizNameLower = (business.name || "").toLowerCase().trim();
  const bizAddressLower = (business.address || "").toLowerCase().trim();
  const bizPhoneClean = (business.phone || "").replace(/\D/g, "");

  let bizCityLower = "";
  if (business.address) {
    const parts = business.address.split(",");
    if (parts.length >= 2) {
      bizCityLower = parts[parts.length - 2].toLowerCase().trim();
    }
  }

  let bizDomain = "";
  if (business.website) {
    try {
      const parsed = new URL(business.website.startsWith("http") ? business.website : `https://${business.website}`);
      bizDomain = parsed.hostname.replace(/^www\./, "").toLowerCase();
    } catch {
      bizDomain = "";
    }
  }

  const textToAnalyze = `${evidenceCandidate.sourceTitle || ""} ${evidenceCandidate.evidenceText || ""} ${evidenceCandidate.sourceUrl || ""}`.toLowerCase();

  // 1. Check Identity Match (Name or Domain)
  const nameWords = bizNameLower.split(/\s+/).filter(w => w.length > 2);
  const matchedWordsCount = nameWords.filter(w => textToAnalyze.includes(w)).length;
  const nameWordRatio = nameWords.length > 0 ? matchedWordsCount / nameWords.length : 0;

  const domainMatch = Boolean(bizDomain && textToAnalyze.includes(bizDomain));
  const exactNameMatch = textToAnalyze.includes(bizNameLower);

  if (!domainMatch && !exactNameMatch && nameWordRatio < 0.6) {
    return {
      matchesIdentity: false,
      locationScope: "unclear",
      confidence: 0,
      rejectionReason: `Evidence text does not match business identity "${business.name}".`
    };
  }

  // 2. Check Location Scope & Distant Location Rejection
  let locationScope: HiringEvidence["locationScope"] = "unclear";
  let confidence = 0.5;

  const mentionsExactAddress = Boolean(bizAddressLower && (textToAnalyze.includes(bizAddressLower) || (evidenceCandidate.referencedAddress && evidenceCandidate.referencedAddress.toLowerCase().includes(bizAddressLower))));
  
  const titleAndBodyText = `${evidenceCandidate.sourceTitle || ""} ${evidenceCandidate.evidenceText || ""}`.toLowerCase();
  const cityRegex = bizCityLower ? new RegExp(`\\b${bizCityLower.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')}\\b`, 'i') : null;
  const mentionsCity = Boolean(cityRegex && (cityRegex.test(titleAndBodyText) || (evidenceCandidate.referencedCity && evidenceCandidate.referencedCity.toLowerCase() === bizCityLower)));

  const mentionsPhone = Boolean(bizPhoneClean && bizPhoneClean.length >= 7 && textToAnalyze.replace(/\D/g, "").includes(bizPhoneClean));

  if (mentionsExactAddress || mentionsPhone || (mentionsCity && exactNameMatch)) {
    locationScope = "exact_location";
    confidence = mentionsExactAddress || mentionsPhone ? 0.95 : 0.85;
  } else if (mentionsCity) {
    locationScope = "regional";
    confidence = 0.70;
  } else if (domainMatch || exactNameMatch) {
    // Check if evidence mentions a completely different distant city
    if (evidenceCandidate.referencedCity && bizCityLower && evidenceCandidate.referencedCity.toLowerCase() !== bizCityLower) {
      return {
        matchesIdentity: false,
        locationScope: "unclear",
        confidence: 0,
        rejectionReason: `Evidence belongs to a different location (${evidenceCandidate.referencedCity}) rather than ${bizCityLower}.`
      };
    }
    locationScope = "company_wide";
    confidence = 0.50;
  } else {
    locationScope = "unclear";
    confidence = 0.30;
  }

  return {
    matchesIdentity: true,
    locationScope,
    confidence
  };
}

/**
 * Extracts and synthesizes verified local hiring evidence for a candidate business.
 */
export function extractAndVerifyHiringEvidence(
  business: BusinessBusiness,
  publicCrawlData?: any
): HiringEvidence[] {
  const verifiedEvidences: HiringEvidence[] = [];
  const now = new Date().toISOString();
  const bizId = business.id;
  const bizName = business.name || "Business";
  const locationId = business.zipcode || null;

  // 1. Check Official Website / Careers Page Evidence
  const careersUrl = business.hiringPageUrl || (business.websiteAudit as any)?.hiringPageUrl;
  if (careersUrl || (business.websiteAudit && (business.websiteAudit as any).careersPageFound)) {
    const rawUrl = careersUrl || `${business.website}/careers`;
    const match = verifyEvidenceMatch(
      {
        sourceTitle: `${bizName} Official Careers & Jobs Page`,
        evidenceText: `Active careers section found on official website for ${bizName}.`,
        sourceUrl: rawUrl
      },
      business
    );

    if (match.matchesIdentity) {
      const pubDate = (business.websiteAudit as any)?.lastAudited || (business as any).source_published_at || null;
      const upDate = (business.websiteAudit as any)?.lastAudited || (business as any).source_updated_at || null;
      const dSource = pubDate ? "page_metadata" : "unknown";
      const dConf = pubDate ? 0.75 : 0.20;
      const firstSeenAt = business.last_checked_at || now;
      const lastSeenAt = now;
      const lastCheckedAt = now;

      verifiedEvidences.push({
        businessId: bizId,
        businessName: bizName,
        locationId,
        sourceType: "official_careers_page",
        sourceUrl: rawUrl,
        sourceTitle: `${bizName} Careers Page`,
        evidenceText: `Verified official careers portal active for ${bizName}.`,
        evidenceType: "Careers Page Active",
        locationScope: match.locationScope,
        sourcePublishedAt: pubDate,
        sourceUpdatedAt: upDate,
        firstSeenAt,
        lastSeenAt,
        lastCheckedAt,
        dateSource: dSource,
        dateConfidence: dConf,
        hasSourceDate: !!(pubDate || upDate),
        dates: {
          sourcePublishedAt: pubDate,
          sourceUpdatedAt: upDate,
          firstSeenAt,
          lastSeenAt,
          lastCheckedAt,
          dateSource: dSource,
          dateConfidence: dConf,
          hasSourceDate: !!(pubDate || upDate)
        },
        confidence: match.confidence,
        status: "active"
      });
    }
  }

  // 2. Check Existing Signal Evidence Items or Public Crawl Data
  if (business.signal_evidence_items && Array.isArray(business.signal_evidence_items)) {
    business.signal_evidence_items.forEach((rawItem) => {
      const item = rawItem as any;
      const evType = (item.evidenceType || item.type || "").toString().toLowerCase();
      const factText = (item.fact || item.detail || "").toString();
      const sTitle = (item.sourceTitle || item.title || factText).toString();
      const sUrl = item.sourceUrl || item.source_url || undefined;
      const sType = item.sourceType || item.source_type || "other_public_source";

      if (
        evType.includes("job") ||
        evType.includes("hiring") ||
        evType.includes("careers") ||
        factText.toLowerCase().includes("hiring") ||
        factText.toLowerCase().includes("openings") ||
        factText.toLowerCase().includes("position")
      ) {
        const match = verifyEvidenceMatch(
          {
            sourceTitle: sTitle,
            evidenceText: factText,
            sourceUrl: sUrl
          },
          business
        );

        if (match.matchesIdentity && match.confidence >= 0.4) {
          const pubDate = item.sourcePublishedAt || item.publishedAt || item.date || null;
          const upDate = item.sourceUpdatedAt || item.updatedAt || item.date || null;
          const dSource = item.dateSource || (sType === "official_job_post" || sType === "job_listing" ? "job_posting_date" : sType === "news" ? "article_date" : "unknown");
          const dConf = item.dateConfidence || (pubDate ? 0.85 : 0.20);
          const firstSeenAt = item.firstSeenAt || now;
          const lastSeenAt = item.lastSeenAt || now;
          const lastCheckedAt = item.lastCheckedAt || now;

          verifiedEvidences.push({
            businessId: bizId,
            businessName: bizName,
            locationId,
            sourceType: sType === "official_job_post" ? "job_listing" : sType === "news" ? "local_news" : "other_public_source",
            sourceUrl: sUrl || business.website || `https://google.com/search?q=${encodeURIComponent(bizName)}`,
            sourceTitle: sTitle || `Public Hiring Record for ${bizName}`,
            evidenceText: factText,
            evidenceType: item.evidenceType || item.type || "Public Job Listing",
            locationScope: match.locationScope,
            sourcePublishedAt: pubDate,
            sourceUpdatedAt: upDate,
            firstSeenAt,
            lastSeenAt,
            lastCheckedAt,
            dateSource: dSource,
            dateConfidence: dConf,
            hasSourceDate: !!(pubDate || upDate),
            dates: {
              sourcePublishedAt: pubDate,
              sourceUpdatedAt: upDate,
              firstSeenAt,
              lastSeenAt,
              lastCheckedAt,
              dateSource: dSource,
              dateConfidence: dConf,
              hasSourceDate: !!(pubDate || upDate)
            },
            confidence: match.confidence,
            status: item.status === "expired" ? "expired" : item.status === "unconfirmed" ? "aging" : "active"
          });
        }
      }
    });
  }

  // 3. Check Public Social Media / Google Business Profile / Expansion Updates
  if (business.last_hiring_update) {
    const match = verifyEvidenceMatch(
      {
        sourceTitle: `${bizName} Local Hiring Update`,
        evidenceText: business.last_hiring_update,
        sourceUrl: business.signal_source_url || business.website || undefined
      },
      business
    );

    if (match.matchesIdentity) {
      const pubDate = (business as any).last_hiring_update_date || (business as any).source_published_at || null;
      const upDate = (business as any).last_hiring_update_date || (business as any).source_updated_at || null;
      const dSource = pubDate ? "social_post_date" : "unknown";
      const dConf = pubDate ? 0.85 : 0.20;
      const firstSeenAt = now;
      const lastSeenAt = now;
      const lastCheckedAt = now;

      verifiedEvidences.push({
        businessId: bizId,
        businessName: bizName,
        locationId,
        sourceType: "business_profile_update",
        sourceUrl: business.signal_source_url || business.website || `https://google.com/search?q=${encodeURIComponent(bizName + " hiring")}`,
        sourceTitle: `${bizName} Business Profile Hiring Post`,
        evidenceText: business.last_hiring_update,
        evidenceType: "Local Hiring Update",
        locationScope: match.locationScope,
        sourcePublishedAt: pubDate,
        sourceUpdatedAt: upDate,
        firstSeenAt,
        lastSeenAt,
        lastCheckedAt,
        dateSource: dSource,
        dateConfidence: dConf,
        hasSourceDate: !!(pubDate || upDate),
        dates: {
          sourcePublishedAt: pubDate,
          sourceUpdatedAt: upDate,
          firstSeenAt,
          lastSeenAt,
          lastCheckedAt,
          dateSource: dSource,
          dateConfidence: dConf,
          hasSourceDate: !!(pubDate || upDate)
        },
        confidence: match.confidence,
        status: "active"
      });
    }
  }

  if (business.last_expansion_update) {
    const match = verifyEvidenceMatch(
      {
        sourceTitle: `${bizName} Expansion Announcement`,
        evidenceText: business.last_expansion_update,
        sourceUrl: business.website || undefined
      },
      business
    );

    if (match.matchesIdentity) {
      const pubDate = (business as any).last_expansion_update_date || (business as any).source_published_at || null;
      const upDate = (business as any).last_expansion_update_date || (business as any).source_updated_at || null;
      const dSource = pubDate ? "article_date" : "unknown";
      const dConf = pubDate ? 0.85 : 0.20;
      const firstSeenAt = now;
      const lastSeenAt = now;
      const lastCheckedAt = now;

      verifiedEvidences.push({
        businessId: bizId,
        businessName: bizName,
        locationId,
        sourceType: "expansion_announcement",
        sourceUrl: business.website || `https://google.com/search?q=${encodeURIComponent(bizName + " expansion")}`,
        sourceTitle: `${bizName} Local Expansion Announcement`,
        evidenceText: business.last_expansion_update,
        evidenceType: "Expansion & Growth",
        locationScope: match.locationScope,
        sourcePublishedAt: pubDate,
        sourceUpdatedAt: upDate,
        firstSeenAt,
        lastSeenAt,
        lastCheckedAt,
        dateSource: dSource,
        dateConfidence: dConf,
        hasSourceDate: !!(pubDate || upDate),
        dates: {
          sourcePublishedAt: pubDate,
          sourceUpdatedAt: upDate,
          firstSeenAt,
          lastSeenAt,
          lastCheckedAt,
          dateSource: dSource,
          dateConfidence: dConf,
          hasSourceDate: !!(pubDate || upDate)
        },
        confidence: match.confidence,
        status: "active"
      });
    }
  }

  return verifiedEvidences;
}

/**
 * Calculates hiring signal score and breakdown strictly based on verified evidence.
 * Explicitly rejects generic category / size / rating assumptions if no real evidence is found.
 */
export function calculateEvidenceGroundedHiringScore(
  evidences: HiringEvidence[],
  business: BusinessBusiness
): {
  hiringScore: number;
  hiringBreakdown: string[];
  hiringLikelihood: "High" | "Medium" | "Low";
  verifiedEvidenceCount: number;
} {
  const activeEvidences = evidences.filter(e => e.status === "active" || e.status === "aging");

  if (activeEvidences.length === 0) {
    let cityStr = "";
    if (business.address) {
      const parts = business.address.split(",");
      if (parts.length >= 2) cityStr = ` in ${parts[parts.length - 2].trim()}`;
    }
    return {
      hiringScore: 5,
      hiringBreakdown: [
        `No verified location-specific public hiring evidence found for ${business.name}${cityStr}. (Hiring signal unconfirmed)`
      ],
      hiringLikelihood: "Low",
      verifiedEvidenceCount: 0
    };
  }

  let totalScore = 10;
  const breakdown: string[] = [];

  activeEvidences.forEach(ev => {
    let scoreDelta = 0;
    const scopeLabel =
      ev.locationScope === "exact_location"
        ? "Exact location verified"
        : ev.locationScope === "regional"
        ? "Regional match"
        : ev.locationScope === "company_wide"
        ? "Company-wide page"
        : "Unclear location scope";

    // Weight by location scope:
    if (ev.locationScope === "exact_location") {
      scoreDelta = Math.round(45 * ev.confidence);
    } else if (ev.locationScope === "regional") {
      scoreDelta = Math.round(25 * ev.confidence);
    } else if (ev.locationScope === "company_wide") {
      scoreDelta = Math.round(10 * ev.confidence);
    } else {
      scoreDelta = Math.round(5 * ev.confidence);
    }

    // Adjust for freshness
    if (ev.status === "aging") {
      scoreDelta = Math.round(scoreDelta * 0.8);
    }

    totalScore += scoreDelta;
    breakdown.push(
      `[${ev.sourceType.replace(/_/g, " ")}] ${ev.evidenceText} (${scopeLabel}, +${scoreDelta} pts)`
    );
  });

  const finalScore = Math.max(0, Math.min(100, totalScore));
  const hiringLikelihood = finalScore >= 70 ? "High" : finalScore >= 40 ? "Medium" : "Low";

  return {
    hiringScore: finalScore,
    hiringBreakdown: breakdown,
    hiringLikelihood,
    verifiedEvidenceCount: activeEvidences.length
  };
}
